import { buildCycleColors, type CycleColorMaps } from "../analysis/cycleColor";
import type { SimView } from "../core/simView";
import { lensRegistry, loadBuiltinPlugins } from "../plugins";
import type { DrawContext } from "../plugins/types";
import { effectiveColorMode, type RenderParams } from "../sim/params";
import { projectParticles } from "./camera";
import {
  bucketColor,
  COLOR_BUCKETS,
  normalizeColormap,
  speedToBucket,
} from "./colormap";
import type { Renderer } from "./types";

loadBuiltinPlugins();

export class CanvasRenderer implements Renderer {
  readonly canvas: HTMLCanvasElement;
  lastDrawMs = 0;

  /** Last frame projection — used by pick / composite */
  screenX = new Float64Array(0);
  screenY = new Float64Array(0);
  depth = new Float64Array(0);

  private ctx: CanvasRenderingContext2D;
  private speeds: Float64Array = new Float64Array(0);
  private cssW = 0;
  private cssH = 0;
  private ro: ResizeObserver | null = null;
  private order: Int32Array = new Int32Array(0);

  private buckets: number[][] = Array.from({ length: COLOR_BUCKETS }, () => []);

  private cycleCache: CycleColorMaps | null = null;
  private cycleEpoch = -1;
  private cycleFriendsRef: Int32Array | null = null;

  constructor(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("2D canvas unsupported");
    this.canvas = canvas;
    this.ctx = ctx;
  }

  observe(container: Element): void {
    this.ro?.disconnect();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(container);
    this.resize();
  }

  disconnect(): void {
    this.ro?.disconnect();
    this.ro = null;
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const rect =
      parent?.getBoundingClientRect() ?? this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Size to the stage host only (not stage-wrap + toolbar).
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    this.cssW = w;
    this.cssH = h;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  clear(color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.cssW, this.cssH);
  }

  /** Ensure projection buffers match n and fill from current view. */
  project(view: SimView, render: RenderParams): void {
    const { n, dim, positions } = view;
    if (this.screenX.length !== n) {
      this.screenX = new Float64Array(n);
      this.screenY = new Float64Array(n);
      this.depth = new Float64Array(n);
      this.order = new Int32Array(n);
    }
    projectParticles(
      positions,
      dim,
      n,
      render,
      this.cssW,
      this.cssH,
      this.screenX,
      this.screenY,
      this.depth,
    );
  }

  draw(view: SimView, render: RenderParams): void {
    const t0 = performance.now();
    const { ctx } = this;
    const { n, dim } = view;
    const w = this.cssW;
    const h = this.cssH;
    if (w < 1 || h < 1) {
      this.lastDrawMs = 0;
      return;
    }

    const range = render.plotRange;
    const scale = Math.min(w, h) / (2 * range);
    const cx = w / 2;
    const cy = h / 2;

    this.project(view, render);

    if (render.showTrails) {
      ctx.fillStyle = `rgba(0,0,0,${render.trailFade})`;
      ctx.fillRect(0, 0, w, h);
    } else {
      this.clear(render.background);
    }

    const g: DrawContext = {
      ctx,
      width: w,
      height: h,
      scale,
      cx,
      cy,
      render,
      screenX: this.screenX,
      screenY: this.screenY,
      depth: this.depth,
    };

    for (const lens of lensRegistry.list()) {
      if (lens.always || lens.enabled(render)) {
        lens.draw(view, g);
      }
    }

    const mode = effectiveColorMode(render);
    const baseR = Math.max(0.5, render.pointSize);
    const useRect = render.pointStyle !== "circle";
    const is3d = dim >= 3;

    // Painter's algorithm in 3D: far → near (in-place; avoid Array.from alloc thrash at high n)
    if (this.order.length !== n) this.order = new Int32Array(n);
    for (let i = 0; i < n; i++) this.order[i] = i;
    if (is3d) {
      const dep = this.depth;
      const ord = this.order;
      ord.sort((a, b) => dep[b]! - dep[a]!); // far first (larger depth)
    }

    const radiusAt = (i: number): number => {
      if (!is3d) return baseR;
      // mild size attenuation by depth
      const d = this.depth[i]!;
      const t = 1 / (1 + 0.08 * Math.max(0, d));
      return Math.max(0.4, baseR * (0.55 + 0.45 * t));
    };

    if (mode === "uniform") {
      this.drawOrdered(ctx, n, baseR, useRect, radiusAt, () => "#e2e8f0");
      this.lastDrawMs = performance.now() - t0;
      return;
    }

    if (mode === "cycle") {
      const maps = this.ensureCycleColors(view);
      this.drawOrdered(
        ctx,
        n,
        baseR,
        useRect,
        radiusAt,
        (i) => maps.css[i] ?? "#64748b",
      );
      this.lastDrawMs = performance.now() - t0;
      return;
    }

    if (mode === "type") {
      const types = view.types;
      const palette = [
        "#59bfff",
        "#ff8c40",
        "#73e680",
        "#d973e6",
        "#ffd94d",
        "#66e6e6",
        "#ff738c",
        "#b3b3bf",
      ];
      this.drawOrdered(ctx, n, baseR, useRect, radiusAt, (i) => {
        const t = types ? types[i]! : 0;
        return palette[t % palette.length]!;
      });
      this.lastDrawMs = performance.now() - t0;
      return;
    }

    // velocity — t = clamp(‖v‖ · colorScale); same map as WebGL
    if (this.speeds.length !== n) this.speeds = new Float64Array(n);
    view.writeSpeeds(this.speeds);
    const cmap = normalizeColormap(render.colormap);

    if (!is3d) {
      // Fast bucket path (2D)
      for (let b = 0; b < COLOR_BUCKETS; b++) this.buckets[b]!.length = 0;
      for (let i = 0; i < n; i++) {
        const t = this.speeds[i]! * render.colorScale;
        this.buckets[speedToBucket(t)]!.push(i);
      }
      for (let b = 0; b < COLOR_BUCKETS; b++) {
        const list = this.buckets[b]!;
        if (!list.length) continue;
        ctx.fillStyle = bucketColor(b, cmap);
        this.fillList(ctx, list, baseR, useRect, () => baseR);
      }
    } else {
      this.drawOrdered(ctx, n, baseR, useRect, radiusAt, (i) => {
        const t = this.speeds[i]! * render.colorScale;
        return bucketColor(speedToBucket(t), cmap);
      });
    }

    this.lastDrawMs = performance.now() - t0;
  }

  private drawOrdered(
    ctx: CanvasRenderingContext2D,
    n: number,
    _baseR: number,
    useRect: boolean,
    radiusAt: (i: number) => number,
    colorAt: (i: number) => string,
  ): void {
    let last = "";
    for (let k = 0; k < n; k++) {
      const i = this.order[k]!;
      const c = colorAt(i);
      if (c !== last) {
        ctx.fillStyle = c;
        last = c;
      }
      const r = radiusAt(i);
      const x = this.screenX[i]!;
      const y = this.screenY[i]!;
      if (useRect) ctx.fillRect(x - r, y - r, r * 2, r * 2);
      else {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private fillList(
    ctx: CanvasRenderingContext2D,
    list: number[],
    r: number,
    useRect: boolean,
    radiusAt: (i: number) => number,
  ): void {
    if (useRect) {
      for (let k = 0; k < list.length; k++) {
        const i = list[k]!;
        const rr = radiusAt(i);
        ctx.fillRect(
          this.screenX[i]! - rr,
          this.screenY[i]! - rr,
          rr * 2,
          rr * 2,
        );
      }
    } else {
      for (let k = 0; k < list.length; k++) {
        const i = list[k]!;
        const rr = radiusAt(i);
        ctx.beginPath();
        ctx.arc(this.screenX[i]!, this.screenY[i]!, rr, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    void r;
  }

  private ensureCycleColors(view: SimView): CycleColorMaps {
    const epoch = view.topologyEpoch;
    if (
      this.cycleCache &&
      this.cycleEpoch === epoch &&
      this.cycleFriendsRef === view.friends
    ) {
      return this.cycleCache;
    }
    this.cycleCache = buildCycleColors(view.friends);
    this.cycleEpoch = epoch;
    this.cycleFriendsRef = view.friends;
    return this.cycleCache;
  }
}
